import { FC, useContext, useMemo } from 'react'
import cn from 'classnames'
import themes from 'styles/base/themes.styl'
import styles from 'components/atoms/Flex/Flex.styl'
import Button, { ButtonProps } from 'components/features/pfm/Button/Button'
import Separator from 'components/atoms/Separator/Separator'
import { LabeledElement, LabeledElementList } from 'components/atoms/LabeledElement/LabeledElement'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { useFormContext } from 'react-hook-form'
import { useSelector } from 'react-redux'
import {
  createAutoPaymentRequestSelector,
  createCardPaymentMethodSelector,
  createPaymentRequestSelector,
  currentCCLedgerSelector,
  deleteAutoPaymentRequestSelector,
} from 'components/features/creditCard/creditCardStore/selectors'
import {
  BACK_BUTTON_PROPS,
  PayCardAmountPaneButton,
  PayCardConfirmPaneButton,
  PayCardDatePaneButton,
  PayCardMethodPaneButton,
} from 'components/features/pfm/Button/FloatingPaneButtons'
import Flex from 'components/atoms/Flex/Flex'
import SvgIcon from 'components/atoms/SvgIcon'
import ChevrongSvg from 'icons/chevron.svg'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { formatRelativeDate, formatUSDate } from 'helpers/dateTimeHelpers'
import { DATE_FORMAT_READABLE } from 'constants/datetime'
import { PaneContext } from 'components/features/pfm/FloatingPaneHost/FloatingPaneHost'
import { AutoPayCardPaneType, PayCardPaneType } from 'components/features/creditCard/creditCardStore/floatingPane'
import { DeleteAutoPaymentActionButton } from 'components/features/pfm/Button/ActionButtons'
import { CCCreateCardPaymentRequest } from 'components/features/creditCard/creditCardStore/types'
import { getPaymentAmountTypeTitle, getPaymentMethodTitle } from 'components/features/creditCard/creditCardStore/helpers'


const PayCardConfirmStep: FC<{}> = () => {
  const pane = useContext(PaneContext) as PayCardPaneType | AutoPayCardPaneType
  const isAutoPay = pane?.mode === 'autoPay'
  const isEditMode = !!pane?.uid
  const createPaymentRequest = useStoreSelector(createPaymentRequestSelector)
  const createAutoPayRequest = useStoreSelector(createAutoPaymentRequestSelector)
  const deleteAutoPayRequest = useStoreSelector(deleteAutoPaymentRequestSelector)
  const isLoading = createPaymentRequest.isLoading || createAutoPayRequest.isLoading || deleteAutoPayRequest.isLoading
  const { watch } = useFormContext<CCCreateCardPaymentRequest>()
  const { amount, payment_date, payment_method, payment_amount_type, days_before_due_date } = watch()
  const ledger = useStoreSelector(currentCCLedgerSelector)
  const paymentMethodItem = useSelector(createCardPaymentMethodSelector(payment_method))
  const valueClassNames = cn(styles.flex, styles.verticallyCenter, styles.tiny)
  const chevronChild = useMemo(() => (
    <SvgIcon src={ ChevrongSvg } size='regular' className={ cn(themes.blue, themes.textOnly) } />
  ), [])

  const paymentAmount = useMemo(() => {
    if (payment_amount_type === 'current') {
      return ledger?.current_balance
    } else if (payment_amount_type === 'statement') {
      return ledger?.statement_balance_remaining
    } else if (payment_amount_type === 'minimum') {
      return ledger?.minimum_payment_remaining
    }
    return amount
  }, [amount, ledger, payment_amount_type])

  const paymentAmountTypeText = payment_amount_type === 'fixed' ? 'Fixed Amount' : getPaymentAmountTypeTitle(payment_amount_type)

  const formattedPaymentAmount = useMemo(() => !!paymentAmount && (
    formatUSCurrency(paymentAmount, 'penny')
  ), [paymentAmount])

  const paymentAmountType = useMemo(() => (
    <small>{ paymentAmountTypeText }</small>
  ), [paymentAmountTypeText])

  const clickableValueProps = useMemo(() => ({
    className: valueClassNames,
    disabled: isLoading,
    component: 'div',
    backButton: <PayCardConfirmPaneButton { ...BACK_BUTTON_PROPS } />,
  }), [isLoading, valueClassNames]) as ButtonProps

  const slimMode = isAutoPay && payment_amount_type !== 'fixed'

  const ClickableAmount = useMemo(() => (
    <PayCardAmountPaneButton { ...clickableValueProps }>
      <Flex gap='micro' alignRight>
        <span>{ slimMode ? paymentAmountTypeText : formattedPaymentAmount }</span>
        { !slimMode && paymentAmountType }
      </Flex>
      { chevronChild }
    </PayCardAmountPaneButton>
  ), [chevronChild, clickableValueProps, paymentAmount, paymentAmountType])

  const ClickableDate = useMemo(() => (
    <PayCardDatePaneButton { ...clickableValueProps }>
      <Flex gap='micro' alignRight>
        <span>
          { isAutoPay
            ? days_before_due_date + ' days from due date'
            : !!payment_date && formatUSDate(payment_date, DATE_FORMAT_READABLE)}
        </span>
        <small>
          { isAutoPay
            ? 'every month'
            : !!payment_date && formatRelativeDate(payment_date, DATE_FORMAT_READABLE, 'week')}
        </small>
      </Flex>
      { chevronChild }
    </PayCardDatePaneButton>
  ), [chevronChild, clickableValueProps, days_before_due_date, isAutoPay, payment_date])

  const ClickablePaymentMethod = useMemo(() => {
    if (!paymentMethodItem) {
      return null
    }
    const title = getPaymentMethodTitle(paymentMethodItem)
    return (
      <PayCardMethodPaneButton { ...clickableValueProps }>
        <Flex gap='micro' alignRight>
          <div>{ title } </div>
          <small>{'Ending in ' + paymentMethodItem.number_last_digits } </small>
        </Flex>
        { chevronChild }
      </PayCardMethodPaneButton>
    )
  }, [paymentMethodItem, clickableValueProps, chevronChild])

  const disclaimerChild = useMemo(() => !isAutoPay ? (
    <small>
      By confirming, you are providing your electronic signature and authorizing
      us to debit your account for the one-time payment described above. If your
      payment is returned unpaid, you also authorize us to make a one-time
      electronic fund transfer from your account to collect a fee of $25.
    </small>
  ) : (
    <small>
      By confirming, you are providing your electronic signature and authorizing
      us to debit your account every month as described above. If your payment
      is returned unpaid, you also authorize us to make a one-time electronic
      fund transfer from your account to collect a fee of $25.
    </small>
  ), [paymentAmount])


  return (
    <Flex padding='large-responsive'>
      <section>
        <h2>{ isAutoPay ? 'Auto-pay' : 'Payment' } Summary</h2>
        <p>
          For payments received after the minimum payment due date, the payment
          will be late and may incur a fee.
        </p>
      </section>
      <LabeledElementList>
        <LabeledElement label='Amount' element={ ClickableAmount } />
        <Separator />
        <LabeledElement label='Send On' element={ ClickableDate } />
        <Separator />
        <LabeledElement label='Pay With' element={ ClickablePaymentMethod } />
      </LabeledElementList>
      <Button text={ isAutoPay ? 'Save Auto-pay' : 'Confirm Payment' } fullWidth submit isSubmitting={ isLoading } />
      { isAutoPay && isEditMode && <DeleteAutoPaymentActionButton text='Turn off Auto-Pay' theme='lightRed' isSubmitting={ isLoading } /> }
      { disclaimerChild }
    </Flex>
  )
}

export default PayCardConfirmStep
